import { Component, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { CityModel } from 'src/app/models/management/city.model';
import { CountryService } from 'src/app/services/management/country.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { CityService } from '../../../../services/management/city.service';
import { DepartmentService } from '../../../../services/management/department.service';

@Component({
  selector: 'app-edit-city',
  templateUrl: './register-city.component.html',
  styleUrls: ['./register-city.component.scss'],
  standalone: false
})
export class RegisterCityComponent implements OnInit, OnDestroy {
  cityId: number = 0;
  cityContract: CityModel = new CityModel();
  countryId: number = 0;
  departmentId: number = 0;
  formCity: FormGroup = new FormGroup({});
  listDepartments: any[] = [];
  listDepartmentsByCountry: any[] = [];
  listCountries: any[] = [];

  subscription: Subscription;

  constructor(
    private cityService: CityService,
    public config: DynamicDialogConfig,
    private countryService: CountryService,
    private departmentService: DepartmentService,
    private formBuilder: FormBuilder,
    @Optional() private ref: DynamicDialogRef,
    private toastHandlerService: ToastHandlerService) {
    this.getAllLists();
  }

  ngOnInit(): void {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.cityContract = inputs.cityModel;
      if (this.cityContract && this.cityContract.cityId != 0) {
        this.cityId = this.cityContract.cityId;
      }
      this.validateForm(new CityModel());
      this.handleInitialState();
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  async getAllLists() {
    try {
      const [countries, departments] = await Promise.all([
        firstValueFrom(this.countryService.getAllCountriesForSelect()),
        firstValueFrom(this.departmentService.getAllDepartmentsForSelect()),
      ]);
      this.listCountries = countries;
      this.listDepartments = departments;
      this.listDepartmentsByCountry = departments;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas.', error);
      return false;
    }
  }

  changeCity() {
    this.departmentId = this.formCity.get("departmentId")?.value;
    const code = this.formCity.get("code")?.value;

    if (!this.departmentId || this.departmentId == 0) {
      this.toastHandlerService.showToast('warning', 'Seleccione el páis.');
    }

    if (code != "") {
      this.getCityByCode(this.departmentId, code);
    } else {
      this.createNewCity();
    }
  }

  async createNewCity() {
    this.cityId = 0;
    this.cityContract = new CityModel();
    if (this.departmentId != 0) {
      this.cityContract.departmentId = this.departmentId;
    }
    await this.validateForm(this.cityContract);
  }

  async getDepartment(departmentId: number) {
    this.departmentId = departmentId;
    this.createNewCity();
  }

  async getCityByCode(departmentId: number, code: string) {
    try {
      this.cityContract = await firstValueFrom(this.cityService.getCityByCode(departmentId, code));
      this.cityId = this.cityContract.cityId;
      this.validateForm(this.cityContract);

    } catch (error) {
      console.error('Error al buscar la ciudad por código.', error);
    }
  }

  async getCityById() {
    try {
      this.cityContract = await firstValueFrom(this.cityService.getCityById(this.cityId));
      this.cityId = this.cityContract.cityId;
      await this.validateForm(this.cityContract);
    } catch (error) {
      console.error('Error al buscar la ciudad por id.', error);
    }
  }

  async getCountry(countryId: number) {
    this.countryId = countryId;
    this.listDepartmentsByCountry = this.listDepartments.filter(x => x.countryId == countryId);
    this.validateForm(new CityModel());
  }


  async handleInitialState() {
    if (this.cityId !== null && this.cityId !== 0) {
      await this.getCityById();
    } else {
      this.createNewCity();
    }
  }

  async send() {
    Object.keys(this.formCity.controls).forEach(key => {
      const control = this.formCity.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });

    if (this.formCity.valid) {
      const modelCity = this.formCity.getRawValue();

      if (this.cityId == 0) {
        try {
          const response = await firstValueFrom(this.cityService.createCity(modelCity));
          this.toastHandlerService.showToast('success', `La ciudad fue creada exitosamente.`);
          this.cityContract = response;
          this.cityId = this.cityContract.cityId;
          await this.validateForm(this.cityContract);
        } catch (error) {
          console.error('Error al crear la ciudad.', error);
        }
      } else {
        this.cityContract.name = modelCity.name;
        try {
          const response = await firstValueFrom(this.cityService.updateCity(this.cityContract));
          this.toastHandlerService.showToast('success', `La ciudad fue modificada exitosamente.`);
          this.cityContract = response;
          await this.validateForm(this.cityContract);
        } catch (error) {
          console.error('Error al actualizar la ciudad.', error);
        }
      }
      const output$ = this.config.data?.output$;
      output$?.next({ type: 'reload' });
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      countryId: {
        required: 'El país es obligatirio.'
      },
      departmentId: {
        required: 'El departamento es obligatirio.'
      },
      code: {
        required: 'La código es obligatirio.',
        maxlength: 'El campo código permite máximo 3 caracteres.'
      },
      name: {
        required: 'El nombre es obligatorio.',
        maxlength: 'El campo nombre permite máximo 30 caracteres.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formCity.get(controlName);
      if (control && control.invalid && control.errors) {
        const errors = control.errors;
        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  validateForm(city: CityModel) {
    this.formCity = this.formBuilder.group({
      countryId: [city.department?.countryId, [Validators.required]],
      departmentId: [city.departmentId, [Validators.required, Validators.min(1)]],
      code: [city.code, [Validators.maxLength(3)]],
      name: [city.name, [Validators.required, Validators.maxLength(30)]],

    });

    if (this.countryId != 0) {
      this.formCity.controls["countryId"].setValue(this.countryId);
    }
  }
}
